import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Flex, Card, Heading, Text, Button, Spinner } from "@radix-ui/themes";
import { CheckCircle, XCircle } from "lucide-react";
import { authService } from "@/services/breeze/AuthService";

type ConfirmationState = "loading" | "success" | "error";

export default function ConfirmEmailPage() {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<ConfirmationState>("loading");
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        const emailParam = searchParams.get("email");
        const token = searchParams.get("token");

        if (!emailParam || !token) {
          setErrorMessage("Invalid confirmation link. Missing email or token.");
          setState("error");
          return;
        }

        await authService.confirmEmail(emailParam, token);
        setState("success");
      } catch (error) {
        console.error("Error during email confirmation:", error);
        setErrorMessage(
          error instanceof Error
            ? error.message
            : "Failed to confirm email. Please try again or contact support.",
        );
        setState("error");
      }
    };

    // Only run the callback if we have email and token
    if (searchParams.get("email") && searchParams.get("token")) {
      handleAuthCallback();
    } else {
      setErrorMessage(
        "Invalid confirmation link. Missing required parameters.",
      );
      setState("error");
    }
  }, [searchParams]);

  const handleContinue = () => {
    const redirectUri = searchParams.get("redirect_uri");
    // Only allow relative paths to prevent open redirect attacks
    if (redirectUri && redirectUri.startsWith("/") && !redirectUri.startsWith("//")) {
      window.location.href = redirectUri;
    }
  };

  const renderContent = () => {
    switch (state) {
      case "loading":
        return (
          <Flex direction="column" align="center" gap="4">
            <Spinner size="3" />
            <Heading size="5" weight="medium">
              Verifying your email...
            </Heading>
            <Text size="2" color="gray" align="center">
              Please wait while we confirm your email address.
            </Text>
          </Flex>
        );

      case "success":
        const redirectUri = searchParams.get("redirect_uri");
        return (
          <Flex direction="column" align="center" gap="4">
            <CheckCircle size={48} color="green" />
            <Heading size="5" weight="medium" color="green">
              Email Confirmed!
            </Heading>
            <Text size="2" color="gray" align="center">
              Your email has been successfully verified.
              {redirectUri
                ? " You can now continue to your application."
                : " You can now safely close this page."}
            </Text>
            {redirectUri && (
              <Button
                onClick={handleContinue}
                size="3"
                style={{ marginTop: "8px" }}
              >
                Continue
              </Button>
            )}
          </Flex>
        );

      case "error":
        return (
          <Flex direction="column" align="center" gap="4">
            <XCircle size={48} color="red" />
            <Heading size="5" weight="medium" color="red">
              Confirmation Failed
            </Heading>
            <Text size="2" color="gray" align="center">
              {errorMessage}
            </Text>
            <Text size="2" color="gray" align="center">
              If you believe this is an error, please contact your administrator
              or try requesting a new confirmation email.
            </Text>
            {searchParams.get("redirect_uri") && (
              <Button
                onClick={handleContinue}
                size="3"
                style={{ marginTop: "8px" }}
              >
                Continue to Application
              </Button>
            )}
          </Flex>
        );

      default:
        return null;
    }
  };

  return (
    <Flex height="100vh" align="center" justify="center">
      <Card size="3" style={{ width: "100%", maxWidth: "28rem" }}>
        <Flex direction="column" align="center" gap="3" p="6">
          {renderContent()}
        </Flex>
      </Card>
    </Flex>
  );
}
